import { useEffect, useState } from 'react'
import { FormikConfig } from 'formik'
import { notification } from '@/shared/helpers/notificationMessages'
import { errorHandler } from '@/shared/lib/errorHandler'
import { useEditWebinarApi } from '../api/edit-webinar-form-api'
import { useTranslation } from 'react-i18next'
import { hasAiTranslation, matchTranslations } from '@/shared/lib/utils'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { updateTranslationsForEditing } from '@/shared/lib/updateTranslationsForEditing'
import { EditTranslations } from '@/entities/ContentTranslation/model/types'
import { editWebinarFormDefault } from './consts'
import { EditWebinarFormState } from '../model/types'
import { useWebinarById } from '@/entities/Webinars'
import { hasTranslationsChanged } from '@/entities/ContentTranslation'

export const useEditWebinarForm = (currentWebinarId: number, onCloseModal: () => void) => {
  const [originalTranslations, setOriginalTranslations] = useState<EditTranslations | null>(null)
  const [initialValues, setInitialValues] = useState<EditWebinarFormState>(
    () => editWebinarFormDefault,
  )
  const { language } = useLanguageStore()
  const { t } = useTranslation(['common'])

  const {
    data: currentWebinar,
    isLoading: isLoadingWebinar,
    isError: isErrorWebinar,
  } = useWebinarById(currentWebinarId)

  const {
    mutateAsync,
    isPending: isLoadingEditWebinar,
    isSuccess: isSuccessEditWebinar,
  } = useEditWebinarApi()

  const submitForm: FormikConfig<EditWebinarFormState>['onSubmit'] = async values => {
    const { translations, ...restValues } = values
    const updatedTranslations = updateTranslationsForEditing(translations, originalTranslations)
    const hasAiTranslationFlag = hasAiTranslation(updatedTranslations)
    const shouldUpdateTranslations = hasTranslationsChanged(
      updatedTranslations,
      originalTranslations,
    )

    const updatedValues = {
      ...restValues,
      has_ai_translation: hasAiTranslationFlag,
      translations: updatedTranslations,
      shouldUpdateTranslations,
    }

    try {
      await mutateAsync(updatedValues)
      onCloseModal()
    } catch (error) {
      errorHandler(error, t('common:notification_messages.edit_webinar_error'))
    }
  }

  useEffect(() => {
    if (currentWebinar) {
      setInitialValues(() => {
        const { id, release_date, video_url, translations, thumbnail } = currentWebinar
        const matchedTranslations = matchTranslations(translations, initialValues.translations)
        setOriginalTranslations({ ...matchedTranslations })
        return {
          release_date: release_date ? new Date(release_date as string) : null,
          video_url,
          translations: matchedTranslations,
          thumbnail,
          id,
        }
      })
    }
  }, [currentWebinar])

  useEffect(() => {
    if (isSuccessEditWebinar) {
      notification.success(
        t('common:notification_titles.success'),
        t('common:notification_messages.edit_webinar_success'),
      )
    }
  }, [isSuccessEditWebinar])

  return {
    initialValues,
    isLoadingEditWebinar,
    isLoadingWebinar,
    isErrorWebinar,
    submitForm,
  }
}
